import React, { useEffect, useState } from "react";
import {
    Collapse,
    Card,
    Spin,
    Alert,
    Table,
    Tooltip,
    Checkbox,
    Select,
    Input,
    Button,
    message
} from "antd";
import { EditOutlined, ReloadOutlined } from "@ant-design/icons";
import { info, error as logAppInsightError } from '../../utils/logger';
import { logError } from "../../utils/logError";
import { getAccessToken } from "../../services/authApi";
import { fieldReader, fieldWriter, createTask } from "../../services/loanApi";
import { hasPageAccess } from "../../services/personaApi";

const { Panel } = Collapse;
const { TextArea } = Input;
const { Option } = Select;
// Returns ISO string in PST (America/Los_Angeles)

const getPstNow = () => {
    // Get PST time parts
    const pstDate = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Los_Angeles' }));
    const pad = (n) => n.toString().padStart(2, '0');
    let hours = pstDate.getHours();
    const minutes = pad(pstDate.getMinutes());
    const seconds = pad(pstDate.getSeconds());
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12;
    return `${pad(pstDate.getMonth() + 1)}/${pad(pstDate.getDate())}/${pstDate.getFullYear()} ${pad(hours)}:${minutes}:${seconds} ${ampm}`;
};

const API_DOMAIN = process.env.REACT_APP_TPOC_API_DOMAIN;
const AMDocReviewPage = () => {
    const [loading, setLoading] = useState(true);
    const [reviewError, setReviewError] = useState(null);
    const [errorType, setErrorType] = useState(null);
    const [loanID, setLoanID] = useState();
    const [loanNumber, setLoanNumber] = useState(null);

    /** NEW STATES **/
    const [reviewed, setReviewed] = useState(false);
    const [reviewStatus, setReviewStatus] = useState();
    const [notes, setNotes] = useState("");
    const [saving, setSaving] = useState(false);

    const [historyTableData, setHistoryTableData] = useState([]);
    // NEW – track selected row for review
    const [activeReviewRow, setActiveReviewRow] = useState(null);
    const [reviewContextValue, setReviewContextValue] = useState(null);
    const [actionLoading, setActionLoading] = useState(false);

    const [canEdit, setCanEdit] = useState(false);
    const [permissionMessage, setPermissionMessage] = useState("");

    useEffect(() => {
        const boot = async () => {
            if (window.elli?.script?.guest) {
                window.elli.script.guest.create();

                // persona gating
                try {
                    const authObj = await window.elli.script.getObject("auth");
                    const user = await authObj.getUser();

                    const allowed = await hasPageAccess("AMDocReviewPage", user);

                    setCanEdit(allowed);

                    if (!allowed) {
                        setPermissionMessage(
                            ""
                        );
                    } else {
                        setPermissionMessage("");
                    }
                } catch (e) {
                    console.warn("Failed to read auth user:", e);
                    setCanEdit(false);
                    setPermissionMessage(
                        ""
                    );
                }

                initializeAuthObject();
            } else {
                console.warn("elli.script.guest is not available.");
            }
        };

        boot();
    }, []);


    const initializeAuthObject = async () => {
        try {
            window.elli.script.getObject("loan").then(async function (loanObject) {
                loanObject.all().then(async function (loan) {
                    try {
                        setLoanID(loan?.loan?.id);
                        setLoanNumber(loan?.loan?.loanNumber);

                        //Reading custom field value to show if exception is already submitted
                        const tpoAuthObject = await window.elli.script.getObject("auth");
                        const authCode = await tpoAuthObject.createAuthCode();
                        const reToken = await getAccessToken(authCode);
                        const allFieldIds = Array.from(new Set([
                            "CX.AMCOND.DOCS.REVIEWHISTORY"
                        ]));
                        setLoanID(loan?.loan?.id);
                        await refreshReviewHistory(loan?.loan?.id);
                        setLoading(false);
                    } catch (err) {
                        setReviewError(err.message || "Failed to fetch snapshot");
                        setErrorType("Error");
                    }
                    finally {
                        setLoading(false);
                    }
                });
            });
        } catch (error) {
            const errorCode = logError("CONDITION_SNAPHOT_ERROR");
            logAppInsightError("Condition Snapshot Initialization Failed", {
                component: "Condition Snapshot",
                problemId: errorCode,
                errorCode,
                rawError: error,
                errorMessage: `${errorCode} - Failed to load Condition Snapshot Information.`
            });
        }
    };

    const refreshReviewHistory = async (loan) => {
        try {

            setReviewError(null);            // ✅ reset
            setHistoryTableData([]);         // ✅ reset
            const tpoAuthObject = await window.elli.script.getObject("auth");
            const authCode = await tpoAuthObject.createAuthCode();
            const reToken = await getAccessToken(authCode);

            const fieldResponse = await fieldReader(
                reToken,
                ["CX.AMCOND.DOCS.REVIEWHISTORY"],
                loan
            );

            const historyData = fieldResponse["CX.AMCOND.DOCS.REVIEWHISTORY"];

            let parsedHistory = [];
            try {
                parsedHistory = historyData ? JSON.parse(historyData) : [];
            } catch (e) {
                console.error("Invalid history JSON", e);
            }

            if (parsedHistory.length == 0) {
                setReviewError("No Documents to review");
                return;
            }
            // ✅ SORT by SubmittedDate (latest first)
            parsedHistory.sort(
                (a, b) => new Date(b.SubmittedDate) - new Date(a.SubmittedDate)
            );
            setHistoryTableData(parsedHistory);
        } catch (err) {
            console.error("Failed to refresh review history", err);
            message.error("Failed to refresh review history");
        }
    };

    const startReviewIfNeeded = async (row) => {
        if (!canEdit) {
            message.warning(permissionMessage || "You don’t have permission to review.");
            return false;
        }

        try {
            setActionLoading(true);

            const tpoAuthObject = await window.elli.script.getObject("auth");
            const authCode = await tpoAuthObject.createAuthCode();
            const reToken = await getAccessToken(authCode);

            // 🔹 Read full review history
            const readResponse = await fieldReader(
                reToken,
                ["CX.AMCOND.DOCS.REVIEWHISTORY"],
                loanID
            );

            let history = [];
            try {
                history = JSON.parse(
                    readResponse["CX.AMCOND.DOCS.REVIEWHISTORY"] || "[]"
                );
            } catch {
                history = [];
            }

            // 🔹 Find matching row
            const rowIndex = history.findIndex(
                (h) => h.RowNumber === row.RowNumber
            );

            if (rowIndex === -1) {
                message.error("Review record not found");
                return false;
            }

            const updatedHistory = [...history];


            const tpoAuthObjects = await window.elli.script.getObject("auth");
            const authCodes = await tpoAuthObjects.createAuthCode();
            const reTokens = await getAccessToken(authCodes);
            // 🔹 Set ReviewStartDate ONLY if missing
            if (!updatedHistory[rowIndex].ReviewStartDate) {
                updatedHistory[rowIndex] = {
                    ...updatedHistory[rowIndex],
                    ReviewStartDate: getPstNow()
                };

                await fieldWriter(
                    reTokens,
                    loanID,
                    [
                        {
                            id: "CX.AMCOND.DOCS.REVIEWHISTORY",
                            value: JSON.stringify(updatedHistory)
                        }
                    ]
                );
            }

            // 🔹 Refresh grid to show ReviewStartDate
            await refreshReviewHistory(loanID);

            return true;
        } catch (err) {
            console.error("Failed to start review", err);
            message.error("Unable to start review");
            return false;
        } finally {
            setActionLoading(false);
        }
    };


    // Helper to format date as MM/DD/YYYY hh:mm:ss AM/PM
    const formatDateWithAmPm = (val) => {
        if (!val) return "-";
        const date = new Date(val);
        const pad = (n) => n.toString().padStart(2, '0');
        let hours = date.getHours();
        const minutes = pad(date.getMinutes());
        const seconds = pad(date.getSeconds());
        const ampm = hours >= 12 ? 'PM' : 'AM';
        hours = hours % 12;
        hours = hours ? hours : 12; // 0 should be 12
        return `${pad(date.getMonth() + 1)}/${pad(date.getDate())}/${date.getFullYear()} ${pad(hours)}:${minutes}:${seconds} ${ampm}`;
    };

    const historyColumns = [
        {
            title: "Row #",
            dataIndex: "RowNumber",
            key: "RowNumber",
            width: 80
        },
        {
            title: "Review Submitted Date",
            dataIndex: "SubmittedDate",
            key: "SubmittedDate",
            render: (val) => formatDateWithAmPm(val)
        },
        // {
        //     title: "Review Submitted By",
        //     dataIndex: "SubmittedBy",
        //     key: "SubmittedBy"
        // },
        {
            title: "Documents To Review",
            dataIndex: "DocumentsToReview",
            key: "DocumentsToReview",
            render: (val, record) => {
                const files = record?.Attachments || [];
                return (
                    <Tooltip
                        title={
                            files.length > 0 ? (
                                <div className="list-disc pl-4">
                                    {files.map((f, i) => (
                                        <li key={i}>{f.FileName}</li>
                                    ))}
                                </div>
                            ) : "No files"
                        }
                    >
                        <a href="#!" onClick={(e) => e.preventDefault()}>
                            {record?.Attachments?.length}
                        </a>
                    </Tooltip>
                );
            }
        },
        {
            title: "Review Start Date",
            dataIndex: "ReviewStartDate",
            key: "ReviewStartDate",
            render: (val) => formatDateWithAmPm(val)
        },
        {
            title: "Review Completed Date",
            dataIndex: "ReviewCompletedDate",
            key: "ReviewCompletedDate",
            render: (val) => formatDateWithAmPm(val)
        },
        {
            title: "Reviewed By",
            dataIndex: "ReviewedBy",
            key: "ReviewedBy",
            render: (val) => val || "-"
        },
        {
            title: "SLA",
            key: "reviewDuration",
            render: (_, record) => calculateReviewDuration(record)
        },
        {
            title: "Status",
            dataIndex: "Status",
            key: "Status",
            render: (val) => {
                if (!val) return "-";
                const status = val.toLowerCase();
                if (status === "approved") return "Eligible for review";
                if (status === "returned") return "Not Eligible for review";
                return val;
            }
        },
        {
            title: "Notes",
            dataIndex: "Notes",
            key: "Notes",
            render: (val) => val || "-"
        },
        {
            title: "Action",
            key: "action",
            align: "center",
            render: (_, record) => {
                const isCompleted = !!record.ReviewCompletedDate;
                return (
                    <Tooltip
                        title={
                            !canEdit
                                ? (permissionMessage || "View-only access")
                                : (isCompleted ? "Review already completed" : "Add Review")
                        }
                    >
                        <Button
                            type="link"
                            icon={<EditOutlined />}
                            loading={actionLoading && activeReviewRow?.RowNumber === record.RowNumber}
                            disabled={isCompleted || actionLoading || !canEdit}
                            onClick={async () => {
                                if (!canEdit) return;
                                setActiveReviewRow(record);
                                const success = await startReviewIfNeeded(record);
                                if (!success) {
                                    setActiveReviewRow(null);
                                    return;
                                }
                                setReviewed(false);
                                setNotes("");
                                setReviewStatus(undefined);
                            }}
                        />
                    </Tooltip>
                );
            }
        }
    ];

    const calculateReviewDuration = (record) => {
        if (!record.SubmittedDate) return "-";

        const start = new Date(formatDateWithAmPm(record.SubmittedDate)); // PST
        const end = record.ReviewCompletedDate
            ? new Date(formatDateWithAmPm(record.ReviewCompletedDate)) // PST
            : new Date(new Date(getPstNow())); // now (PST)

        const diffMs = Math.abs(end.getTime() - start.getTime());

        const hrs = Math.floor(diffMs / (1000 * 60 * 60));
        const mins = Math.floor(
            (diffMs % (1000 * 60 * 60)) / (1000 * 60)
        );

        return `${hrs}h ${mins}m`;
    };





    const saveReviewToApi = async ({
        resourceId,
        rowNumber,
        status,
        notes
    }) => {
        const tpoAuthObject = await window.elli.script.getObject("auth");
        const authCode = await tpoAuthObject.createAuthCode();
        const user = await tpoAuthObject.getUser();
        const reviewedBy = `${user.firstName} ${user.lastName}`;

        const response = await fetch(
            `${API_DOMAIN}/api/conditions/update-review`,
            {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({
                    authCode,
                    resourceId,
                    rowNumber,
                    reviewedBy,
                    status,
                    notes,

                })
            }
        );

        if (!response.ok) {
            const err = await response.text();
            throw new Error(err || "Failed to save review");
        }

        return response.json();
    };


    const handleSave = async () => {
        if (!reviewStatus) {
            message.warning("Please select Review Status");
            return;
        }

        try {
            setSaving(true);
            await saveReviewToApi({
                resourceId: loanID,
                rowNumber: activeReviewRow.RowNumber, // ✅ SEND ROW ID
                status: reviewStatus,
                notes
            });

            message.success("Review saved successfully");

            // Reset fields
            setReviewed(false);
            setNotes("");
            setReviewStatus(undefined);
            setActiveReviewRow(null);

            // Refresh review history (this is what needs to happen after saving)
            await refreshReviewHistory(loanID);

        } catch (err) {
            console.error(err);
            message.error(err.message || "Failed to save review");
        } finally {
            setSaving(false);
        }
    };


    return (
        <>

            {loading ? (
                <div className="flex justify-center items-center h-40">
                    <Spin size="large" />
                </div>
            ) :
                reviewError ? (
                    <div className="flex flex-col items-center min-h-screen bg-gray-50 p-6">
                        {/* REVIEW ERROR - TOP BOX */}
                        {reviewError && (
                            <div className="bg-white shadow-md rounded-lg p-8 max-w-lg w-full text-center border border-gray-200 mb-6">
                                <div className="flex justify-center mb-4">
                                    <svg
                                        className="w-12 h-12 text-yellow-500"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M12 8v4m0 4h.01M12 2a10 10 0 100 20 10 10 0 000-20z"
                                        />
                                    </svg>
                                </div>
                                <h2 className="text-xl font-semibold text-gray-800 mb-3">
                                    Review Required
                                </h2>
                                <p className="text-gray-600">{reviewError}</p>
                            </div>
                        )}

                        {/* Spacer to push bottom box slightly up */}
                        <div className="flex-grow" />
                    </div>
                )

                    : (

                        <>
                            {/* =====================
                        REVIEW SECTION
                        ====================== */}
                            {/* REVIEW SAVE SECTION – ONLY AFTER ACTION CLICK */}
                            {activeReviewRow && (
                                <Card
                                    className="mb-4"
                                    title={`Reviewing Row #${activeReviewRow.RowNumber} - Document submitted on ${new Date(activeReviewRow.SubmittedDate).toLocaleString()} `}
                                >
                                    {actionLoading ? (
                                        <div className="flex justify-center py-8">
                                            <Spin />
                                        </div>
                                    ) : (
                                        <div className="space-y-3">

                                            <div className="flex items-center gap-3">
                                                <span className="w-32 font-medium text-gray-600">
                                                    Reviewed <span className="text-red-500">*</span>:
                                                </span>
                                                <Checkbox
                                                    checked={reviewed}
                                                    disabled={!canEdit}
                                                    onChange={(e) => {
                                                        setReviewed(e.target.checked);
                                                        if (e.target.checked && !reviewStatus) {
                                                            setReviewStatus("Approved");
                                                        }
                                                        if (!e.target.checked) {
                                                            setReviewStatus(undefined);
                                                        }
                                                    }}
                                                />
                                            </div>

                                            <div className="flex items-center gap-3">
                                                <span className="w-32 font-medium text-gray-600">
                                                    Review Status <span className="text-red-500">*</span>:
                                                </span>
                                                <Select
                                                    value={reviewStatus}
                                                    onChange={setReviewStatus}
                                                    placeholder="Select"
                                                    style={{ width: 220 }}
                                                    allowClear
                                                    disabled={!reviewed || !canEdit}
                                                >
                                                    <Option value="Approved">Eligible for review</Option>
                                                    <Option value="Returned">Not Eligible for review</Option>
                                                </Select>
                                            </div>

                                            <div className="flex items-start gap-3">
                                                <span className="w-32 font-medium text-gray-600 pt-1">
                                                    Notes:
                                                </span>
                                                <div>
                                                    <TextArea
                                                        rows={2}
                                                        style={{ width: 400 }}
                                                        disabled={!canEdit}
                                                        value={notes}
                                                        onChange={(e) => setNotes(e.target.value)}
                                                    />

                                                    <div className="mt-2 flex gap-2">
                                                        <Button
                                                            type="primary"
                                                            loading={saving}
                                                            onClick={handleSave}
                                                            disabled={!reviewed || !reviewStatus || saving || !canEdit}
                                                            style={{
                                                                backgroundColor: "#006aa9",
                                                                borderColor: "#006aa9",
                                                                color: "whitesmoke",
                                                                width: 120
                                                            }}
                                                        >
                                                            Save
                                                        </Button>
                                                        <Button
                                                            onClick={() => {
                                                                setActiveReviewRow(null);
                                                                setReviewed(false);
                                                                setReviewStatus(undefined);
                                                                setNotes("");
                                                            }}
                                                        >
                                                            Cancel
                                                        </Button>
                                                    </div>
                                                </div>
                                            </div>

                                        </div>
                                    )}
                                </Card>
                            )}

                            {/* REVIEW HISTORY – ALWAYS VISIBLE */}
                            {historyTableData && historyTableData.length > 0 ? (
                                <Card
                                    className="mb-4"
                                    title={
                                        <div className="flex justify-between items-center">
                                            <span>Review History</span>
                                            <Tooltip title="Refresh">
                                                <Button
                                                    icon={<ReloadOutlined />}
                                                    size="small"
                                                    onClick={() => refreshReviewHistory(loanID)}
                                                />
                                            </Tooltip>
                                        </div>
                                    }
                                >

                                    <Table
                                        rowKey="RowNumber"
                                        columns={historyColumns}
                                        dataSource={historyTableData}
                                        pagination={false}
                                        size="small"
                                        bordered
                                    />
                                </Card>
                            ) : (
                                <Card className="mb-4" title="Review History">
                                    <p className="text-gray-500 text-center">No document to review</p>
                                </Card>
                            )}

                            <div className="border-t border-gray-200 my-3" />
                        </>
                    )
            }
        </>
    );

};

export default AMDocReviewPage;
